import React, { useState, useEffect } from 'react'
import axios from 'axios'
import newarrivalStyles from './NewArrival.module.css'
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Image from 'react-bootstrap/Image'
import Button from 'react-bootstrap/Button'
import { Link, useNavigate } from 'react-router-dom'

const NewArrival = ({ 
    category = "all", 
    title,
    maxProducts = 5,
    apiBaseUrl = process.env.REACT_APP_API_BASE || 'http://localhost:5000/api' 
}) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate(); // Hook for programmatic navigation

    // Slider settings
    // Update the slider settings in NewArrival component
const settings = {
    dots: false,
    arrows: false,
    infinite: false,
    speed: 500,
    slidesToShow: Math.min(maxProducts, 5),
    slidesToScroll: 1,
    initialSlide: 0,
    responsive: [
        {
            breakpoint: 1400,
            settings: {
                slidesToShow: Math.min(maxProducts, 4),
                slidesToScroll: 1,
                infinite: true,
                dots: false
            }
        },
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: Math.min(maxProducts, 3),
                slidesToScroll: 1,
                infinite: true,
                dots: false
            }
        },
        {
            breakpoint: 768,
            settings: {
                slidesToShow: Math.min(maxProducts, 2),
                slidesToScroll: 1,
                initialSlide: 1
            }
        },
        {
            breakpoint: 480,
            settings: {
                slidesToShow: Math.min(maxProducts, 2),
                slidesToScroll: 1
            }
        }
    ]
};

    // Function to handle product click
    const handleProductClick = (productId) => {
        navigate(`/shop/product/${productId}`);
    };

    // Function to handle "View All" button click with category selection
    const handleViewAllClick = () => {
        if (category && category !== "all") {
            // Navigate to shop with category parameter
            navigate(`/shop?category=${encodeURIComponent(category)}`);
        } else {
            // Navigate to regular shop page for "all" category
            navigate('/shop');
        }
    };

    // Function to get display title
    const getDisplayTitle = () => {
        // If title prop is provided, use it
        if (title) {
            return title;
        }
        
        // Otherwise use category name, formatted nicely
        if (category === "all") {
            return "ALL PRODUCTS";
        }
        
        // Convert category to title case (e.g., "cat1" -> "Cat1")
        return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
    };
    
    const getRandomProducts = (productsArray, count) => {
        const shuffled = [...productsArray].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, count);
    };

    // Function to fetch products by category using axios
    const fetchProductsByCategory = async (categoryName) => {
        try {
            setLoading(true);
            setError(null);
            
            // Skip API call if category is "all" and you don't want to fetch all products
            if (categoryName === "all") {
                // Option 1: Skip the API call for "all" category
                setProducts([]);
                setLoading(false);
                return;
                
                // Option 2: If you want to fetch all products, modify the endpoint:
                // const endpoint = `${apiBaseUrl}/product/getAllProducts`;
            } else {
                // Construct API endpoint based on your structure
                const endpoint = `${apiBaseUrl}product/getProductsByCategories`;
                
                // Make the API call with category parameter
                const response = await axios.get(endpoint, {
                    params: {
                        category: categoryName // This will create: ?category=cat1
                    },
                    timeout: 10000, // 10 second timeout
                    headers: {
                        'Content-Type': 'application/json',
                        // Add authentication headers if needed
                        // 'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    }
                });
                
                // Handle response data
                let fetchedProducts = response.data;
                
                // Handle different response structures your API might return
                if (response.data.data) {
                    // If API returns { success: true, data: [...] }
                    fetchedProducts = response.data.data;
                } else if (response.data.products) {
                    // If API returns { products: [...], total: 100 }
                    fetchedProducts = response.data.products;
                } else if (Array.isArray(response.data)) {
                    // If API returns direct array: [...]
                    fetchedProducts = response.data;
                }
                
                // Transform API data to match component structure
                const transformedProducts = fetchedProducts.map(product => ({
                    id: product.id || product._id,
                    // Fix the name mapping - use Title (capital T) from your API
                    name: product.Title || product.name || product.title || product.productName,
                    // Fix the image mapping - get first image from image_link array
                    image: (product.image_link && Array.isArray(product.image_link) && product.image_link.length > 0) 
                        ? product.image_link[0] 
                        : product.image || product.images?.[0] || product.thumbnail || product.imageUrl,
                    rating: product.rating || product.averageRating || 4.5,
                    price: product.price || product.salePrice || product.currentPrice,
                    originalPrice: product.originalPrice || product.regularPrice || product.mrp,
                    discount: product.discount || (
                        product.originalPrice && product.price 
                            ? Math.round(((product.originalPrice - product.price) / product.price) * 100)
                            : null
                    ),
                    // Fix category mapping - use Category (capital C) from your API
                    category: product.Category || product.category || product.categoryName,
                    slug: product.slug || product._id || product.id
                }));
                
                console.log('Transformed products:', transformedProducts); // Debug log
                
                // Get random products
                const randomProducts = getRandomProducts(transformedProducts, maxProducts);
                
                setProducts(randomProducts);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            
            // Handle different types of errors
            if (error.response) {
                // Server responded with error status
                const statusCode = error.response.status;
                const errorMessage = error.response.data?.message || error.response.data?.error;
                
                if (statusCode === 404) {
                    setError(`No products found for category: ${categoryName}`);
                } else if (statusCode === 500) {
                    setError('Server error. Please try again later.');
                } else {
                    setError(errorMessage || `Request failed with status ${statusCode}`);
                }
            } else if (error.request) {
                // Network error
                setError('Network error. Please check your internet connection.');
            } else {
                // Other error
                setError('Something went wrong. Please try again.');
            }
            
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log("Link ", process.env.REACT_APP_API_BASE)
        fetchProductsByCategory(category);
    }, [category, maxProducts]);

    // Render product card with navigation
    const renderProductCard = (product) => (
        <div key={product.id}>
            <div 
                className={`${newarrivalStyles.slidercard} ${newarrivalStyles.clickable || 'clickable-product'}`}
                onClick={() => handleProductClick(product.id)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleProductClick(product.id);
                    }
                }}
                aria-label={`View details for ${product.name}`}
            >
                <Image 
                    src={product.image} 
                    alt={product.name}
                    onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                    }}
                />
            </div>
            <div 
                className='pt-3'
                onClick={() => handleProductClick(product.id)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleProductClick(product.id);
                    }
                }}
            >
                <p className={`${newarrivalStyles.slidercardtitle} fw-bold m-0 mb-1`}>
                    {product.name}
                </p>
                <h5 className={`${newarrivalStyles.sliderprice} fw-semibold ${product.originalPrice ? 'd-flex' : ''}`}>
                    ${product.price}
                    {product.originalPrice && (
                        <>
                            <span className={`${newarrivalStyles.slideroldprice} text-decoration-line-through px-2`}>
                                ${product.originalPrice}
                            </span>
                            <span className={newarrivalStyles.sliderdiscountbg}>
                                <span className={newarrivalStyles.sliderdiscounttext}>
                                    -{product.discount}%
                                </span>
                            </span>
                        </>
                    )}
                </h5>
            </div>
        </div>
    );

    if (loading) {
        return (
            <section className={newarrivalStyles.newarrivalcontainer}>
                <Container fluid className='py-5'>
                    <Row>
                        <Col>
                            <div className='text-center'>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading products...</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        );
    }

    if (error) {
        return (
            <section className={newarrivalStyles.newarrivalcontainer}>
                <Container fluid className='py-5'>
                    <Row>
                        <Col>
                            <div className='text-center'>
                                <h2 className={newarrivalStyles.newarrivaltitle}>{getDisplayTitle()}</h2>
                                <div className="alert alert-warning mt-3" role="alert">
                                    {error}
                                </div>
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => fetchProductsByCategory(category)}
                                    className="mt-2"
                                >
                                    Try Again
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        );
    }

    if (products.length === 0) {
        return (
            <section className={newarrivalStyles.newarrivalcontainer}>
                <Container fluid className='py-5'>
                    <Row>
                        <Col>
                            <div className='text-center'>
                                <h2 className={newarrivalStyles.newarrivaltitle}>{getDisplayTitle()}</h2>
                                <p>No products found for this category.</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        );
    }

    return (
        <section className={newarrivalStyles.newarrivalcontainer}>
            <Container fluid className='py-5'>
                <Row>
                    <Col>
                        <div className='text-center mb-5'>
                            <h2 className={newarrivalStyles.newarrivaltitle}>{getDisplayTitle()}</h2>
                        </div>
                        <div className="slider-container" id='newarrivalslider'>
                            <Slider {...settings}>
                                {products.map(product => renderProductCard(product))}
                            </Slider>
                        </div>
                        <div className='text-center py-5'>
                            {/* Updated "View All" button to use the new handler */}
                            <Button 
                                variant="outline-dark" 
                                className={newarrivalStyles.viewbtn}
                                onClick={handleViewAllClick}
                            >
                                View All
                            </Button>
                        </div>
                        <svg width="1240" height="1" viewBox="0 0 1240 1" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="-4.37114e-08" y1="0.500122" x2="1240" y2="0.500014" stroke="black" strokeOpacity="0.1" />
                        </svg>
                    </Col>
                </Row>
            </Container>
        </section>
    )
}

export default NewArrival